import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  parseEquipmentQR,
  type ParticipantEquipmentAssignment,
} from 'shared';
import QRScanner from '@/components/QRScanner';
import { useCheckOutCentro, useCurrentCentro } from '@/hooks/useCentroVisits';
import { useEquipmentsByCentro } from '@/hooks/useEquipments';
import { useMyBadges } from '@/hooks/useParticipantBadges';
import {
  useMyActiveReservations,
  useScanEquipment,
} from '@/hooks/useReservations';
import {
  CERTIFICATION_REQUIRED_MESSAGE,
  checkOutErrorMessage,
  EQUIPMENT_NOT_IN_CENTRO_MESSAGE,
  scanErrorMessage,
} from '@/lib/errors';
import { describeTimeLeft } from '@/lib/reservationTime';
import { useAuthStore } from '@/stores/auth';

type Banner =
  | { kind: 'success'; message: string }
  | { kind: 'error'; message: string }
  | null;

// Shorten the freshness window for this screen so that records updated on the
// backend (centro visit, open reservations, equipment state) surface on the
// next focus/navigation refetch instead of after the hooks' longer defaults.
const LIVE_STALE_TIME_MS = 5_000;

/**
 * Narrow the opaque router `location.state` to a banner handed over by ScanPage
 * after an equipment scan. Returns null for any other navigation.
 */
function bannerFromNavState(state: unknown): Banner {
  if (!state || typeof state !== 'object') return null;
  const candidate = (state as { scanBanner?: unknown }).scanBanner;
  if (!candidate || typeof candidate !== 'object') return null;
  const { kind, message } = candidate as { kind?: unknown; message?: unknown };
  if ((kind === 'success' || kind === 'error') && typeof message === 'string') {
    return { kind, message };
  }
  return null;
}

/** Whether ScanPage handed over a return, after which leaving is offered. */
function offerLeaveFromNavState(state: unknown): boolean {
  return !!state && typeof state === 'object' && (state as { offerLeave?: unknown }).offerLeave === true;
}

const formatTime = (iso: string): string => {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
};

// How often the "restantes" figure is recomputed on screen. Nothing ends the
// session at `expiresAt`: past it the text asks the participant to return it.
const COUNTDOWN_TICK_MS = 30_000;

export default function ReservationsPage() {
  const { user } = useAuthStore();
  const location = useLocation();
  const currentQuery = useCurrentCentro(LIVE_STALE_TIME_MS);
  const activeCentro = currentQuery.data ?? null;

  const participantId = typeof user?.id === 'number' ? user.id : undefined;
  const reservationsQuery = useMyActiveReservations(participantId, LIVE_STALE_TIME_MS);
  const equipmentsQuery = useEquipmentsByCentro(activeCentro?.digitalCenterId, LIVE_STALE_TIME_MS);
  const badgesQuery = useMyBadges(participantId);
  const scanEquipment = useScanEquipment();
  const checkOut = useCheckOutCentro();
  const navigate = useNavigate();

  const [scannerOpen, setScannerOpen] = useState(false);
  // Seed the banner from router state when the green nav button routed an
  // equipment scan here (see ScanPage). The state is consumed once below so a
  // refresh or back-navigation doesn't resurrect a stale banner.
  const [banner, setBanner] = useState<Banner>(
    () => bannerFromNavState(location.state),
  );

  // Set after a return. The question waits for the refreshed list, and is
  // only asked once nothing else is out: a visit cannot end with equipment
  // still held (see HomePage).
  const [offerLeave, setOfferLeave] = useState(() => offerLeaveFromNavState(location.state));
  const [leaveError, setLeaveError] = useState<string | null>(null);

  useEffect(() => {
    if (bannerFromNavState(location.state) || offerLeaveFromNavState(location.state)) {
      window.history.replaceState({}, '');
    }
    // Only intended to run for the banner present at mount; location.state is a
    // one-shot handoff and is cleared here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reservationsForCentro = useMemo(() => {
    if (!activeCentro || !reservationsQuery.data) return [];
    // The Go API marshals empty result slices as JSON `null`; treat as empty
    // so downstream `.length` / `.map` never throw and crash the page.
    return reservationsQuery.data.assignments ?? [];
  }, [activeCentro, reservationsQuery.data]);

  const handleEquipmentScan = async (qr: string) => {
    setScannerOpen(false);
    if (!activeCentro || !participantId) {
      return;
    }

    setBanner(null);
    setOfferLeave(false);

    const equipmentId = parseEquipmentQR(qr);
    if (equipmentId === null) {
      setBanner({ kind: 'error', message: 'QR não reconhecido. Use o QR do equipamento.' });
      return;
    }

    // PWA-only guard: refuse equipment that doesn't belong to this Centro before
    // hitting the backend. Only enforced once the Centro's equipment list has
    // loaded; on a cold cache we fall through and let the backend validate.
    const known = equipmentsQuery.data?.equipments;
    const scanned = known?.find((eq) => eq.id === equipmentId);
    if (known && !scanned) {
      setBanner({ kind: 'error', message: EQUIPMENT_NOT_IN_CENTRO_MESSAGE });
      return;
    }

    // PWA-only certification guard: when the equipment requires a badge, ensure
    // the participant holds it (not revoked) before hitting the backend. Only
    // enforced once both the equipment and badge lists have loaded; otherwise we
    // fall through and let the backend (source of truth) enforce it.
    if (scanned?.badgeId && badgesQuery.data) {
      const hasBadge = badgesQuery.data.badges.some(
        (b) => b.badgeId === scanned.badgeId && !b.revokedAt,
      );
      if (!hasBadge) {
        setBanner({ kind: 'error', message: CERTIFICATION_REQUIRED_MESSAGE });
        return;
      }
    }

    try {
      const result = await scanEquipment.mutateAsync({ equipmentId });
      const equipmentName = result.equipment.equipamento;
      setBanner({
        kind: 'success',
        message:
          result.action === 'started'
            ? `Reserva criada para ${equipmentName}.`
            : `Devolveu o ${equipmentName}.`,
      });
      setOfferLeave(result.action === 'ended');
    } catch (err) {
      setBanner({ kind: 'error', message: scanErrorMessage(err) });
    }
  };

  const handleLeave = async () => {
    setLeaveError(null);
    try {
      await checkOut.mutateAsync();
      setOfferLeave(false);
      navigate('/', { replace: true });
    } catch (err) {
      setLeaveError(checkOutErrorMessage(err));
    }
  };

  const isPending = scanEquipment.isPending;
  const askToLeave =
    offerLeave &&
    !!activeCentro &&
    !!reservationsQuery.data &&
    !reservationsQuery.isFetching &&
    reservationsForCentro.length === 0;

  const showNoCentroState = !currentQuery.isLoading && !activeCentro;

  return (
    <>
      <Helmet>
        <title>Reservas | AICD</title>
      </Helmet>

      <div className="space-y-6">
        <header>
          <h1 className="text-2xl font-black text-foreground">Reservas</h1>
          <p className="mt-1 subtitle text-sm text-muted-foreground">
            {activeCentro
              ? `Equipamentos reservados em ${activeCentro.digitalCenterName}.`
              : 'Gere as suas reservas de equipamento.'}
          </p>
        </header>

        {showNoCentroState && <NoCentroCard />}

        {activeCentro && (
          <>
            <button
              type="button"
              onClick={() => {
                setBanner(null);
                setScannerOpen(true);
              }}
              disabled={isPending}
              className="relative w-full overflow-hidden rounded-3xl bg-gradient-to-br from-[#512f89] to-[#6740a8] p-5 text-left text-white shadow-lg transition-transform active:scale-[0.99] disabled:opacity-70"
            >
              <span
                aria-hidden="true"
                className="pointer-events-none absolute -right-12 -top-12 h-44 w-44 rounded-full bg-[#4ab065]/30 blur-3xl"
              />
              <div className="relative flex items-center gap-4">
                <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl border border-white/20 bg-white/10">
                  <ScanGlyph />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-base font-black tracking-tight">Ler Equipamento</p>
                  <p className="mt-0.5 text-xs text-white/80">
                    Aponte a câmara para o código QR do equipamento que quer reservar.
                  </p>
                </div>
                <span className="flex-shrink-0 text-[#4ab065]">
                  <ArrowGlyph />
                </span>
              </div>
            </button>

            {isPending && (
              <p
                role="status"
                className="rounded-xl border border-border bg-input-soft p-3 text-sm text-muted-foreground"
              >
                A validar equipamento…
              </p>
            )}

            {banner && (
              <p
                role={banner.kind === 'error' ? 'alert' : 'status'}
                className={
                  banner.kind === 'error'
                    ? 'rounded-xl border border-busy/50 bg-busy-soft p-3 text-sm text-busy'
                    : 'rounded-xl border border-available/60 bg-available-soft p-3 text-sm text-available'
                }
              >
                {banner.message}
              </p>
            )}

            <ReservationList
              isLoading={reservationsQuery.isLoading}
              isError={reservationsQuery.isError}
              reservations={reservationsForCentro}
            />
          </>
        )}
      </div>

      {scannerOpen && (
        <QRScanner
          onDecoded={handleEquipmentScan}
          onCancel={() => setScannerOpen(false)}
          title="Ler QR do Equipamento"
          promptHint="Toque para iniciar a câmara e ler o código QR do equipamento."
          instructions="Aponte a câmara para o código QR do equipamento para criar a reserva."
        />
      )}

      {askToLeave && activeCentro && (
        <LeaveCentroDialog
          centroName={activeCentro.digitalCenterName}
          leaving={checkOut.isPending}
          error={leaveError}
          onStay={() => {
            setOfferLeave(false);
            setLeaveError(null);
          }}
          onLeave={handleLeave}
        />
      )}
    </>
  );
}

interface LeaveCentroDialogProps {
  centroName: string;
  leaving: boolean;
  error: string | null;
  onStay: () => void;
  onLeave: () => void;
}

/** After returning the last equipment: end the visit too, in one tap. */
function LeaveCentroDialog({ centroName, leaving, error, onStay, onLeave }: LeaveCentroDialogProps) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="leave-dialog-title"
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 px-5"
    >
      <div className="w-full max-w-sm rounded-3xl bg-surface p-6 shadow-lg">
        <h2 id="leave-dialog-title" className="text-lg font-extrabold text-foreground">
          Também vai sair do Centro?
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Já não tem equipamento consigo. Pode terminar já a visita a{' '}
          <span className="font-semibold">{centroName}</span>.
        </p>
        {error && (
          <p role="alert" className="mt-3 rounded-xl border border-busy/50 bg-busy-soft p-3 text-sm text-busy">
            {error}
          </p>
        )}
        <div className="mt-5 flex gap-2">
          <button
            type="button"
            onClick={onStay}
            disabled={leaving}
            className="flex-1 rounded-xl border border-border bg-input-soft px-4 py-3 text-sm font-bold text-muted-foreground"
          >
            Ficar
          </button>
          <button
            type="button"
            onClick={onLeave}
            disabled={leaving}
            className="flex-1 rounded-xl bg-available px-4 py-3 text-sm font-bold text-white shadow disabled:opacity-60"
          >
            {leaving ? 'A terminar…' : 'Sair do Centro'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface ReservationListProps {
  isLoading: boolean;
  isError: boolean;
  reservations: ParticipantEquipmentAssignment[];
}

function ReservationList({ isLoading, isError, reservations }: ReservationListProps) {
  const [now, setNow] = useState(() => Date.now());
  const hasCountdown = reservations.some((r) => !!r.expiresAt);
  useEffect(() => {
    if (!hasCountdown) return;
    const id = setInterval(() => setNow(Date.now()), COUNTDOWN_TICK_MS);
    return () => clearInterval(id);
  }, [hasCountdown]);

  return (
    <section aria-labelledby="reservas-heading">
      <div className="mb-3 flex items-center justify-between px-1">
        <h2
          id="reservas-heading"
          className="text-xs font-bold uppercase tracking-widest text-muted-foreground"
        >
          As suas reservas
        </h2>
        {!isLoading && !isError && (
          <span className="rounded-md bg-input-soft px-2 py-0.5 text-xs font-semibold text-muted-foreground">
            {reservations.length}
          </span>
        )}
      </div>

      {isLoading && <ReservationSkeleton />}

      {isError && !isLoading && (
        <div
          role="alert"
          className="rounded-2xl border border-busy/50 bg-busy-soft p-4 text-sm text-busy"
        >
          Não foi possível carregar as reservas. Tente novamente mais tarde.
        </div>
      )}

      {!isLoading && !isError && reservations.length === 0 && (
        <div className="rounded-2xl border border-dashed border-border-strong bg-card p-6 text-center text-sm text-muted-foreground">
          Ainda não tem reservas neste Centro.
        </div>
      )}

      {reservations.length > 0 && (
        <ul className="space-y-2">
          {reservations.map((reservation) => (
            <li
              key={reservation.id}
              className="flex items-center gap-3 rounded-2xl border border-border bg-card p-3 shadow-sm"
            >
              <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <EquipmentGlyph />
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold text-foreground">
                  {reservation.equipmentName}
                </p>
                <p className="truncate text-xs text-muted-foreground">
                  Desde {formatTime(reservation.startedAt)}
                </p>
                {describeTimeLeft(reservation.expiresAt, now) && (
                  <p className="truncate text-xs font-semibold text-busy">
                    {describeTimeLeft(reservation.expiresAt, now)}
                  </p>
                )}
              </div>
              <span className="rounded-md bg-available-soft px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-available">
                Ativa
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ReservationSkeleton() {
  return (
    <ul className="space-y-2">
      {[0, 1, 2].map((i) => (
        <li
          key={i}
          className="flex items-center gap-3 rounded-2xl border border-border bg-card p-3"
        >
          <div className="h-11 w-11 flex-shrink-0 animate-pulse rounded-2xl bg-input-soft" />
          <div className="flex-1 space-y-2">
            <div className="h-3 w-2/3 animate-pulse rounded bg-input-soft" />
            <div className="h-2 w-1/3 animate-pulse rounded bg-input-soft" />
          </div>
        </li>
      ))}
    </ul>
  );
}

function NoCentroCard() {
  return (
    <div className="card p-6 text-center">
      <div
        className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary"
        aria-hidden="true"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          className="h-6 w-6"
        >
          <path d="M6 4a2 2 0 012-2h8a2 2 0 012 2v18l-6-3-6 3V4z" />
        </svg>
      </div>
      <h2 className="text-base font-bold text-foreground">Faça check-in num Centro</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Para criar e gerir reservas, comece por ler o QR de um Centro.
      </p>
      <Link
        to="/"
        className="mt-4 inline-flex items-center justify-center rounded-xl bg-primary px-4 py-2 text-sm font-bold text-primary-foreground shadow"
      >
        Ir para Início
      </Link>
    </div>
  );
}

function ScanGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth={2}>
      <path d="M4 7V5a1 1 0 011-1h2" strokeLinecap="round" />
      <path d="M20 7V5a1 1 0 00-1-1h-2" strokeLinecap="round" />
      <path d="M4 17v2a1 1 0 001 1h2" strokeLinecap="round" />
      <path d="M20 17v2a1 1 0 01-1 1h-2" strokeLinecap="round" />
      <path d="M4 12h16" strokeLinecap="round" />
    </svg>
  );
}

function ArrowGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2.5}>
      <path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function EquipmentGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2}>
      <rect x="3" y="5" width="18" height="12" rx="2" strokeLinejoin="round" />
      <path d="M8 21h8" strokeLinecap="round" />
      <path d="M12 17v4" strokeLinecap="round" />
    </svg>
  );
}
